/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import RateAnalysis, { type RateAnalysisData, type RateAnalysisPayload } from "./RateAnalysis";
import { ESTIMATE_LEVEL_MEASUREMENT_UNIT } from "@Actions/estimation";
import { useEffect, useState } from "react";
import type { Dispatch } from "react";
import _ from "lodash";
import { Trans, useTranslation } from "@i18n";
import useLayoutModuleAccess from "@Services/useLayoutModuleAccess";
import { useDispatch } from "react-redux";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import IconComponent from "@CubeComponents/icons";
import { ADDON_CODE_TO_LAYOUT_FLAG, ADDONS_DISPLAY_NAME, RATE_ANALYSIS_ADDON_CODE } from "@Helpers/constants";
import type { AnyAction } from "@reduxjs/toolkit";
import type { EstimateCurrency } from "@ServerTypes/estimates/estimates.schema";

type CurrencyDetails = EstimateCurrency;

type BoqItemDetails = {
  rateAnalysis: RateAnalysisPayload;
  isRateAnalysisEnable?: boolean;
  rate?: number;
  code?: string;
  hasChild?: boolean;
};

type NumericArgs = {
  value: number | null;
  previousValue?: number | null;
  event?: { stopPropagation?: () => void };
  target: { value: string; name?: string };
};
type UpdateItemDetailsArgs = NumericArgs | { checked: boolean } | RateAnalysisPayload;

type BoqItemRateDetailsProps = {
  itemCurrentDetails: BoqItemDetails;
  currencyDetails: CurrencyDetails;
  updateItemDetails: (args: UpdateItemDetailsArgs, field?: string, checkNumberIsPositive?: boolean) => void;
  isActionPermission: boolean;
};

const BoqItemRateDetails = ({
  itemCurrentDetails,
  currencyDetails,
  updateItemDetails,
  isActionPermission,
}: BoqItemRateDetailsProps) => {
  const [rateAnalysisData, setRateAnalysisData] = useState(
    _.cloneDeep(itemCurrentDetails.rateAnalysis.rateAnalysisData),
  );
  const [purchaseUnitCount, setPurchaseUnitCount] = useState(itemCurrentDetails.rateAnalysis.purchaseUnitCount);
  const [analysisChanged, setAnalysisChanged] = useState<boolean | null>(null);
  const addonsAccess = useLayoutModuleAccess(ADDON_CODE_TO_LAYOUT_FLAG[RATE_ANALYSIS_ADDON_CODE]);

  const { t } = useTranslation();
  const currencySymbol = currencyDetails.selectedSymbol?.name ?? "";
  const dispatch = useDispatch<Dispatch<AnyAction>>();

  useEffect(() => {
    if (analysisChanged !== null) {
      updateItemDetails({ rateAnalysisData, purchaseUnitCount }, "rateAnalysis");
    }
  }, [analysisChanged]);

  const updateRateAnalysisInItem = (data: RateAnalysisData) => {
    setRateAnalysisData(data);
    setAnalysisChanged((prev) => !prev);
  };

  const toggleClickHandler = (args: { checked: boolean }) => {
    if (addonsAccess.allowed) {
      updateItemDetails(args, "isRateAnalysisEnable");
    } else {
      dispatch(
        showToastNotification({
          content: t("addons_access_text_normal", { name: ADDONS_DISPLAY_NAME[RATE_ANALYSIS_ADDON_CODE] }),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  return (
    <div className="flex flex-col gap-8 pt-5 w-full">
      {addonsAccess.isReady && (
        <>
          <div className="flex flex-col gap-4">
            {itemCurrentDetails.isRateAnalysisEnable && !addonsAccess.allowed && (
              <div className="flex gap-4 text-sm px-4 py-2 rounded bg-system_colour_47 border border-banner_color_yellow">
                <IconComponent name="FileConversionStatusInProgress" width="1.25rem" height="1.25rem" />
                <div>
                  <Trans
                    i18nKey="addons_access_text"
                    values={{ name: ADDONS_DISPLAY_NAME[RATE_ANALYSIS_ADDON_CODE] }}
                    components={{ bold: <strong className="font-semibold" /> }}
                  />
                </div>
              </div>
            )}
            <div
              className={
                itemCurrentDetails.isRateAnalysisEnable
                  ? "flex gap-5 justify-between items-center"
                  : "flex flex-col gap-5"
              }
            >
              <div className="flex items-center gap-5">
                <div className="text-base font-semibold flex gap-1">
                  <span>{t("Rate")}</span>
                  <span>({currencySymbol})</span>
                </div>
                <NumericTextBoxComponent
                  width="12.5rem"
                  showSpinButton={false}
                  placeholder="Enter Rate"
                  value={itemCurrentDetails.rate}
                  onChange={(args: NumericArgs) => updateItemDetails(args, "rate")}
                  disabled={
                    itemCurrentDetails.isRateAnalysisEnable ||
                    !isActionPermission ||
                    Boolean(itemCurrentDetails.code) ||
                    Boolean(itemCurrentDetails.hasChild)
                  }
                />
              </div>
              <div className="flex gap-5 items-center">
                <span className="text-base font-semibold">{t("Based on Rate Analysis")}</span>
                <ToggleSwitchComponent
                  checked={itemCurrentDetails.isRateAnalysisEnable}
                  onChange={toggleClickHandler}
                  disabled={
                    !isActionPermission || Boolean(itemCurrentDetails.code) || Boolean(itemCurrentDetails.hasChild)
                  }
                />
              </div>
            </div>
          </div>
          {itemCurrentDetails.isRateAnalysisEnable && !itemCurrentDetails.hasChild && (
            <div className="flex overflow-auto">
              <RateAnalysis
                rateAnalysisItemsData={{ checkedRecord: itemCurrentDetails }}
                currencyData={currencyDetails}
                viewMode={!addonsAccess.allowed || !isActionPermission || Boolean(itemCurrentDetails.code)}
                source={ESTIMATE_LEVEL_MEASUREMENT_UNIT}
                showAnalysisOnly
                parentRateAnalysis={rateAnalysisData}
                setParentRateAnalysis={updateRateAnalysisInItem}
                parentRateAnalysisUnitCount={purchaseUnitCount}
                setParentRateAnalysisUnitCount={setPurchaseUnitCount}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BoqItemRateDetails;
